<script setup>
import { PAGE_ANCHORS } from '@/constants/GlobalConstants'
import { PDP_REQUEST_URL } from '@/constants/PdpConstants'
import { useCartStore } from '@/stores/cart'
import { useCustomerStore } from '@/stores/customer'
import { usePdpStore } from '@/stores/pdp'
import { storeToRefs } from 'pinia'
import {
  computed,
  defineAsyncComponent,
  getCurrentInstance,
  onMounted,
  ref,
  watch
} from 'vue'

const TileWrapper = defineAsyncComponent(
  () => import('@/components/basic/organisms/TileWrapper.vue')
)

const _getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const [
      { default: ApiController },
      { default: UtilityHelper },
      { default: DomHelper },
      { default: MetaProduct }
    ] = await Promise.all([
      await import('@/services/ApiController'),
      await import('@/services/helpers/UtilityHelper'),
      await import('@/services/helpers/DomHelper'),
      await import('@/modules/product/MetaProduct')
    ])
    cache = { ApiController, UtilityHelper, DomHelper, MetaProduct }
    return cache
  }
})()

const props = defineProps({
  ignoreProductId: {
    type: [String, Number],
    default: null
  }
})

const instance = getCurrentInstance()
const translate = instance?.proxy?.translate ?? ((key) => key)

const customerStore = useCustomerStore()
const pdpStore = usePdpStore()
const cartStore = useCartStore()

const { customerIsAdmin } = storeToRefs(customerStore)
const { storedLastUpdate } = storeToRefs(cartStore)

const product = computed(() => pdpStore.getApiProduct)

const productInCart = computed(() => cartStore.productInCart)

const crossSells = ref([])
const crossSellsRef = ref(null)
const crossSellsAnchor = ref(PAGE_ANCHORS.CROSS_SELLS)

async function getCrossSells() {
  // product resets to null ids while the PDP store refreshes; a cart update in
  // that window would otherwise request cross-sells/null/null (VIP-12034).
  // Snapshot the ids: the store can reset them during the module import await.
  const { product_id: productId, campaign_id: campaignId } = product.value ?? {}
  if (!productId || !campaignId) return
  if (!productInCart.value(productId)) return

  const { ApiController, UtilityHelper } = await _getModules()
  const url = UtilityHelper.formatString(
    PDP_REQUEST_URL.META.CROSS_SELLS,
    productId,
    campaignId
  )

  const data = await ApiController.fetchProducts(url, customerIsAdmin.value)
  await setupCrossSells(data)
  if (crossSellsRef.value) {
    await new Promise((resolve) => setTimeout(resolve, 300)) // Wait for component animation
    // ref can become null during the animation delay (e.g. navigation away)
    crossSellsRef.value?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}

async function setupCrossSells(products) {
  if (products.length < 3) {
    return
  }
  const { MetaProduct } = await _getModules()

  crossSells.value = products.map(
    (product) =>
      new MetaProduct({
        product_id: product.product_id,
        campaign_id: product.campaign_id
      })
  )
}

onMounted(async () => {
  await getCrossSells()
})

watch(storedLastUpdate, () => {
  getCrossSells()
})
</script>

<template>
  <div
    ref="crossSellsRef"
    v-if="crossSells.length"
    class="position-relative"
    item-list-id="slider_crosssells"
    data-elbcontext="shopping:discover;product_slider:crosssells"
  >
    <TileWrapper
      :id="crossSellsAnchor"
      :products="crossSells"
      class="pt-5"
      :title="translate('pdp_section_recently_crosssells')"
    />
  </div>
</template>
